import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { MessageSquare, Lock } from "lucide-react";
import { api } from "../../lib/api";

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// Dashboard nav's whisper indicator — used to be a static, non-interactive badge. Now a real
// button: hovering (desktop) or tapping (mobile, since hover doesn't exist on touch) previews
// your most recent secret conversations across every table, and clicking either the button or
// a specific thread jumps straight into that game's Whispers tab with that conversation open.
export function WhisperInboxButton({ unreadCount }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState(null); // null = not loaded yet

  const openAndLoad = () => {
    setOpen(true);
    if (threads === null) api.get("/games/inbox").then((r) => setThreads(r.data)).catch(() => setThreads([]));
  };

  const goTo = (thread) => {
    setOpen(false);
    navigate(`/game/${thread.game_id}?tab=whispers&with=${thread.other_member_id}`);
  };

  return (
    <Popover open={open} onOpenChange={(v) => (v ? openAndLoad() : setOpen(false))}>
      <div onMouseEnter={openAndLoad} onMouseLeave={() => setOpen(false)}>
        <PopoverTrigger asChild>
          <button
            data-testid="dashboard-whisper-inbox-button"
            onClick={openAndLoad}
            title="Your whisper inbox"
            className="relative flex items-center text-[#f1e0c5]/50 hover:text-[#D4AF37] transition-colors duration-150"
          >
            <MessageSquare className="w-4 h-4" />
            {unreadCount > 0 && (
              <span data-testid="dashboard-unread-whispers-badge" className="absolute -top-2 -right-2 bg-[#D90429] text-white text-[9px] leading-none rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent data-testid="whisper-inbox-dropdown" align="end" className="w-80 bg-[#1e1a14] border-white/10 text-[#f1e0c5] p-0 max-h-96 overflow-y-auto">
          <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-wider text-[#f1e0c5]/60">Whisper Inbox</span>
          </div>
          {threads === null ? (
            <p className="text-xs text-[#f1e0c5]/40 text-center py-6">Loading…</p>
          ) : threads.length === 0 ? (
            <p className="text-xs text-[#f1e0c5]/40 text-center py-6" data-testid="whisper-inbox-empty">No secret conversations yet.</p>
          ) : (
            threads.map((t) => (
              <button
                key={`${t.game_id}-${t.other_member_id}`}
                data-testid={`whisper-inbox-thread-${t.game_id}-${t.other_member_id}`}
                onClick={() => goTo(t)}
                className="w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/5 transition-colors duration-150 flex flex-col gap-0.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold truncate">{t.other_name}</span>
                  {t.unread_count > 0 && (
                    <span className="bg-[#D90429] text-white text-[9px] leading-none rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center shrink-0">{t.unread_count}</span>
                  )}
                </div>
                <p className="text-[11px] text-[#f1e0c5]/40 truncate">{t.game_name} · {timeAgo(t.created_at)}</p>
                <p className="text-xs text-[#f1e0c5]/60 truncate">{t.preview}</p>
              </button>
            ))
          )}
        </PopoverContent>
      </div>
    </Popover>
  );
}
